import { createElement, useState } from 'react';
import { Platform, StyleSheet, Text, View } from 'react-native';
import { useTheme, SPACING, TYPOGRAPHY } from '../../theme';
import { Button } from '../common/Button';
import { ErrorBanner } from '../common/ErrorBanner';
import { Input } from '../common/Input';
import { MAX_BID_AMOUNT } from '../../schemas/firestoreSchemas';
import { EMAIL_SHAPE } from '../../utils/peopleLists';
import { useDefensePlansStore } from '../../store/defensePlansStore';
import { HONEYPOT_FIELD_NAME } from '../event/EventSignupForm';
import type { BidError, BidResult } from '../../api/planBids';

const NAME_MAX = 80;
const EMAIL_MAX = 254;
const PHONE_MAX = 40;
const NOTE_MAX = 500;

// Off screen, not display:none, for the same reason as the event sign-up's
// honeypot (EventSignupForm.tsx): a bot that skips hidden fields still sees
// it, a person never reaches it. Same DOM name, same `website` request key.
const HONEYPOT_STYLE = {
  position: 'absolute',
  left: -10000,
  top: 'auto',
  width: 1,
  height: 1,
  overflow: 'hidden',
} as const;

/** "$12,000", "12000" or "12,000.00" to 12000. Null when blank, NaN when not a whole amount. */
export function parseDollars(text: string): number | null {
  const cleaned = text.replace(/[$,\s]/g, '').replace(/\.0+$/, '');
  if (cleaned === '') return null;
  if (!/^\d+$/.test(cleaned)) return Number.NaN;
  return Number(cleaned);
}

/**
 * A vendor's "I can help": who they are, how to reach them, and optionally a
 * price and what they'd do. No account. Sends through the submitBid callable;
 * the server checks everything again.
 */
export function BidForm({
  planId,
  bundleId,
  onSent,
}: {
  planId: string;
  bundleId: string;
  onSent: (result: BidResult) => void;
}) {
  const { colors } = useTheme();
  const submitBid = useDefensePlansStore((s) => s.submitBid);
  const [organization, setOrganization] = useState('');
  const [person, setPerson] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [price, setPrice] = useState('');
  const [note, setNote] = useState('');
  const [website, setWebsite] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const amount = parseDollars(price);
  const orgProblem = organization.trim() ? undefined : 'Add your organization.';
  const personProblem = person.trim() ? undefined : 'Add your name.';
  const emailProblem = EMAIL_SHAPE.test(email.trim()) ? undefined : 'Add an email address.';
  const priceProblem =
    amount != null && (Number.isNaN(amount) || amount > MAX_BID_AMOUNT)
      ? 'Enter whole dollars, like 12000.'
      : undefined;

  const handleSubmit = async () => {
    setSubmitted(true);
    setError(null);
    if (orgProblem || personProblem || emailProblem || priceProblem || sending) return;
    setSending(true);
    try {
      const result = await submitBid({
        planId,
        bundleId,
        organization: organization.trim(),
        person: person.trim(),
        email: email.trim(),
        phone: phone.trim() || undefined,
        amount,
        note: note.trim() || undefined,
        website,
      });
      onSent(result);
    } catch (e) {
      setError((e as BidError).message ?? "That didn't go through. Try again in a moment.");
    } finally {
      setSending(false);
    }
  };

  return (
    <View style={styles.form} testID="bid-form">
      <Input
        label="Organization"
        value={organization}
        onChangeText={(text) => setOrganization(text.slice(0, NAME_MAX))}
        autoCapitalize="words"
        autoComplete="organization"
        textContentType="organizationName"
        maxLength={NAME_MAX}
        error={submitted ? orgProblem : undefined}
        testID="bid-organization"
        containerStyle={styles.field}
      />
      <Input
        label="Your name"
        value={person}
        onChangeText={(text) => setPerson(text.slice(0, NAME_MAX))}
        autoCapitalize="words"
        autoComplete="name"
        textContentType="name"
        maxLength={NAME_MAX}
        error={submitted ? personProblem : undefined}
        testID="bid-person"
        containerStyle={styles.field}
      />
      <Input
        label="Email"
        value={email}
        onChangeText={(text) => setEmail(text.slice(0, EMAIL_MAX))}
        keyboardType="email-address"
        autoCapitalize="none"
        autoCorrect={false}
        autoComplete="email"
        textContentType="emailAddress"
        maxLength={EMAIL_MAX}
        error={submitted ? emailProblem : undefined}
        testID="bid-email"
        containerStyle={styles.field}
      />
      <Input
        label="Phone (optional)"
        value={phone}
        onChangeText={(text) => setPhone(text.slice(0, PHONE_MAX))}
        keyboardType="phone-pad"
        autoComplete="tel"
        textContentType="telephoneNumber"
        maxLength={PHONE_MAX}
        testID="bid-phone"
        containerStyle={styles.field}
      />
      <Input
        label="Estimated price in dollars (optional)"
        value={price}
        onChangeText={(text) => setPrice(text.slice(0, 16))}
        keyboardType="number-pad"
        placeholder="12000"
        error={submitted ? priceProblem : undefined}
        testID="bid-amount"
        containerStyle={styles.field}
      />
      <Input
        label="What you'd do and when (optional)"
        value={note}
        onChangeText={(text) => setNote(text.slice(0, NOTE_MAX))}
        multiline
        numberOfLines={4}
        maxLength={NOTE_MAX}
        hint={`${note.length} of ${NOTE_MAX}`}
        testID="bid-note"
        containerStyle={styles.field}
      />

      {Platform.OS === 'web' && (
        <View aria-hidden pointerEvents="none" style={styles.honeypotWrap}>
          {createElement('input', {
            type: 'text',
            name: HONEYPOT_FIELD_NAME,
            id: HONEYPOT_FIELD_NAME,
            value: website,
            onChange: (e: { target: { value: string } }) => setWebsite(e.target.value),
            tabIndex: -1,
            autoComplete: 'off',
            'aria-hidden': true,
            'data-testid': 'bid-honeypot',
            style: HONEYPOT_STYLE,
          })}
        </View>
      )}

      <Text style={[styles.privacy, { color: colors.textSecondary }]}>
        The plan owner will see what you send here, including your email and phone, so they can
        reach you. Nothing is paid through REDI.
      </Text>

      <ErrorBanner message={error} style={styles.error} testID="bid-error" />

      <Button
        title="I can help"
        onPress={() => void handleSubmit()}
        loading={sending}
        fullWidth
        testID="bid-submit"
      />
    </View>
  );
}

const styles = StyleSheet.create({
  form: {
    gap: SPACING.xs,
  },
  field: {
    marginBottom: SPACING.sm,
  },
  honeypotWrap: {
    position: 'absolute',
    height: 0,
    width: 0,
    overflow: 'hidden',
  },
  privacy: {
    ...TYPOGRAPHY.bodySmall,
    marginBottom: SPACING.sm,
  },
  error: {
    marginBottom: SPACING.sm,
  },
});
